// @flow
import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import ResponsiveImage from 'shared/components/ResponsiveImage';
import CTABlock from './CTABlock';
import TransparentButton from 'shared/components/TransparentButton';
import VideoOverlay from 'shared/components/VideoOverlay';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type Dimensions = {
  height: string,
  width: string,
  aspect_ratio: string
}

export type Image = {
  url: string,
  dimensions: Dimensions,
  tablet_dimensions: Dimensions,
  mobile_dimensions: Dimensions,
  flex_container: boolean,
  flex_content_position: string,
  image_click_url: string,
  zoom_effect: boolean,
  caption: string,
  color_theme: 'DARK' | 'LIGHT',
  title: string,
  title_font: string,
  title_font_size: string,
  title_opacity: string,
  title_line_height: string,
  title_font_weight: string,
  content_padding_top: string,
  title_padding_left_right: string,
  title_position: string,
  description: Array<DescText>,
  buttons: Array<Object>,
  button_type: 'UNDERLINE' | 'STYLED',
  bottom_buttons: BottomButton[]
}

type DescText = {
  text: string
}

export type BottomButton = {
  label: string,
  app_handler: string,
  url: string,
  open_in_new_tab: boolean,
  launch_video: boolean
};

type Props = {
  image: Image,
  imageDisplayType: 'FIXED' | 'ADAPTIVE',
  imageFlexiblePosition: string,
  adaptiveImageBackgroundSize: 'cover' | 'contain',
  responsiveImage?: boolean,
  visible: boolean,
  buttonsPadding: string,
  buttonMinWidth: string
}

// Calculate height/width; default is square, based on viewport width
const getImageSize = (dimensions: Dimensions) => {
  // Preset width or viewport width
  const width = dimensions?.width || Math.max(document.documentElement.clientWidth, window.innerWidth || 0);

  // Preset height or calculate from aspect ratio
  const height = dimensions?.height || parseInt(parseFloat(width) / parseFloat(dimensions?.aspect_ratio || 1));

  return { width, height };
}

export default function SingleImage(props: Props) {
  const {
    image,
    imageDisplayType = 'FIXED',
    responsiveImage,
    visible,
    buttonMinWidth
  } = props;
  // Default true for backwards-compatibility
  const {
    zoom_effect: zoomEffect = true,
    bottom_buttons: buttons,
    color_theme: colorTheme,
  } = image;
  const c: { [key: string]: string } = useStyles(props);

  // Show video in overlay
  const [activeVideoUrl, setActiveVideoUrl] = useState();
  const playVideo = (e, url) => {
    if (url) {
      e.preventDefault();
      setActiveVideoUrl(url);
    }
  };

  // Set height/width matching aspect ratio to prevent reflow and improve lazy loading
  const { height, width } = getImageSize(image?.dimensions);

  const imageComponent = visible && (
    <>
      {
        imageDisplayType === 'FIXED' &&
          <div className={cx(image.image_click_url && zoomEffect && c.zoomWrapper, image.flex_container && c.flexContainer)}>
            { responsiveImage && <ResponsiveImage src={image.url} alt={image?.image_alt || ''} className={c.image} /> }
            { !responsiveImage && <img alt={image?.image_alt || ''} height={height} width={width} src={image.url} className={c.image} /> }
          </div>
      }
      {
        imageDisplayType !== 'FIXED' &&
          <div
            style={{ backgroundImage: `url(${image.url})` }}
            className={cx(c.adaptiveImage, image.image_click_url && zoomEffect && c.zoomWrapper)}
          />
      }
    </>
  );

  // Wrap with anchor, if URL is provided
  if (image.image_click_url) {
    return (
      <a href={image.image_click_url}>
        { imageComponent }
      </a>
    );
  }

  const bottomButtons = (
    <div className={c.btnContainer}>
      <div className={c.btnRowCenter}>
        <div className={c.btnRow}>
          {buttons &&
            buttons.map((btn) => (
              <TransparentButton
                isV2={btn.isV2}
                colorTheme={colorTheme}
                label={btn.label}
                url={btn.url}
                appHandler={btn.app_handler}
                key={btn.label}
                openInNewTab={btn.open_in_new_tab}
                minWidth={buttonMinWidth}
                playVideo={btn.launch_video ? (e) => playVideo(e, btn.url) : undefined}
              />
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className={c.container} key={image.url}>
      { imageComponent }
      { image.caption && <div className={c.caption}>{image.caption}</div> }
      <CTABlock image={image} />
      {bottomButtons}
      <VideoOverlay
        show={!!activeVideoUrl}
        onClose={() => { setActiveVideoUrl(); }}
        url={activeVideoUrl}
      />
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    /*
      Prevent styles that have absolute position from going
      out the container.
    */
    position: 'relative',

    // Use container height for adaptive images
    height: ({ imageDisplayType }) => imageDisplayType === 'ADAPTIVE' ? '100%' : null
  },
  zoomWrapper: {
    overflow: 'hidden',
    position: 'relative',
    '&:after': {
      transition: 'opacity .4s ease',
      content: '""',
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      bottom: '0',
      backgroundColor: '#141618',
      opacity: '0'
    },
    '&:hover': {
      '&> img': {
        transform: 'scale(1.2)'
      },
      '&:after': {
        opacity: '0.5'
      }
    }
  },
  flexContainer: {
    display: 'flex',
    justifyContent: ({ image }) => image?.flex_content_position ? image.flex_content_position : ''
  },
  image: {
    width: ({ image }) => image?.dimensions?.width ? `${image.dimensions.width}px` : '100%',
    height: ({ image }) => image?.dimensions?.height ? `${image.dimensions.height}px` : 'auto',
    transition: 'transform .4s ease',
    display: 'block',
    '@media(max-width: 769px) and (min-width: 429px)': {
      width: ({ image }) => image?.tablet_dimensions?.width ? `${image.tablet_dimensions.width}px` : '100%',
      height: ({ image }) => image?.tablet_dimensions?.height ? `${image.tablet_dimensions.height}px` : 'auto',
    },
    '@media(max-width: 428px)': {
      width: ({ image }) => image?.mobile_dimensions?.width ? `${image.mobile_dimensions.width}px` : '100%',
      height: ({ image }) => image?.mobile_dimensions?.height ? `${image.mobile_dimensions.height}px` : 'auto',
    },
  },
  adaptiveImage: {
    backgroundPosition: ({ imageFlexiblePosition = 'CENTER' }) => imageFlexiblePosition,
    backgroundSize: ({ adaptiveImageBackgroundSize }) => adaptiveImageBackgroundSize || 'cover',
    backgroundRepeat: 'no-repeat',
    height: '100%'
  },
  caption: {
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '17px',
    fontWeight: '600',
    lineHeight: '1.2'
  },
  btnContainer: {
    position: 'absolute',
    width: '100%',
    padding: '0px 12px',
    '@media(min-width: 768px)': {
      padding: '0px 36px',
    },

    bottom: '80px',
    '@media(max-width: 1024px)': {
      bottom: '43px',
    },
  },
  btnRowCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  btnRow: {
    // All buttons should match the size of the largest button
    display: 'grid',
    gridTemplateColumns: ({ buttons }) =>
      buttons?.length ? `repeat(${buttons.length}, 1fr)` : 'none',
    columnGap: ({ buttonsPadding }) =>
      buttonsPadding ? buttonsPadding : '0px',
    marginTop: '24.5px',
  }
});
